import React, { useState } from 'react';
import { ConsultationPhoto } from '../../types/domain';
import { formatLongDate } from '../../lib/monthlyReport';

interface ConsultationPhotoGalleryProps {
  photos: ConsultationPhoto[];
  /** Present for the patient's own gallery; a doctor's view is read-only. */
  onDelete?: (photo: ConsultationPhoto) => Promise<void>;
  emptyText?: string;
}

/**
 * FR-17 photos grouped by consultation date. Shared by the patient's page and
 * the doctor dashboard so both show a visit's photos the same way.
 *
 * Deletion is two taps on the same tile rather than window.confirm(): a
 * blocking browser dialog is jarring in an installed PWA, and the second tap
 * is right where the first one was.
 */
const ConsultationPhotoGallery: React.FC<ConsultationPhotoGalleryProps> = ({
  photos,
  onDelete,
  emptyText = 'Belum ada foto konsultasi.'
}) => {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  if (photos.length === 0) {
    return <div className="subtitle">{emptyText}</div>;
  }

  const groups: { date: string; photos: ConsultationPhoto[] }[] = [];
  for (const photo of photos) {
    const last = groups[groups.length - 1];
    if (last && last.date === photo.consultationDate) {
      last.photos.push(photo);
    } else {
      groups.push({ date: photo.consultationDate, photos: [photo] });
    }
  }

  const handleDelete = async (photo: ConsultationPhoto) => {
    if (!onDelete) {
      return;
    }
    if (confirming !== photo.id) {
      setConfirming(photo.id);
      return;
    }
    setDeleting(photo.id);
    try {
      await onDelete(photo);
    } finally {
      setDeleting(null);
      setConfirming(null);
    }
  };

  return (
    <div className="photo-groups">
      {groups.map(group => (
        <section key={group.date} aria-label={`Konsultasi ${formatLongDate(group.date)}`}>
          <div className="photo-group-date">{formatLongDate(group.date)}</div>
          <ul className="photo-grid">
            {group.photos.map((photo, index) => (
              <li key={photo.id} className="photo-tile">
                {photo.url ? (
                  <a href={photo.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={photo.url}
                      alt={`Foto konsultasi ${formatLongDate(group.date)}, ${index + 1}`}
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div className="photo-missing">Foto tidak tersedia</div>
                )}
                {onDelete ? (
                  <button
                    type="button"
                    className={`photo-delete${confirming === photo.id ? ' confirming' : ''}`}
                    disabled={deleting === photo.id}
                    onClick={() => handleDelete(photo)}
                    onBlur={() => setConfirming(prev => (prev === photo.id ? null : prev))}
                  >
                    {deleting === photo.id
                      ? 'Menghapus...'
                      : confirming === photo.id
                        ? 'Yakin hapus?'
                        : 'Hapus'}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default ConsultationPhotoGallery;
